import { useTranslation } from 'react-i18next';
import { ContractAnalysisReasonEnum } from '@clickersphere/core/enums/ContractAnalysisReasonEnum';
import { ContractAnalysisStatusEnum } from '@clickersphere/core/enums/ContractAnalysisStatusEnum';

interface IAnalysisStatusBannerProps {
  status: ContractAnalysisStatusEnum | null;
  reason: ContractAnalysisReasonEnum | null;
  variableCount: number;
}

const analysisReasonKeys = {
  [ContractAnalysisReasonEnum.NOT_CONFIGURED]:
    'contract.templates.modal.upload.analysis.reason.NOT_CONFIGURED',
  [ContractAnalysisReasonEnum.TIMEOUT]:
    'contract.templates.modal.upload.analysis.reason.TIMEOUT',
  [ContractAnalysisReasonEnum.RATE_LIMITED]:
    'contract.templates.modal.upload.analysis.reason.RATE_LIMITED',
  [ContractAnalysisReasonEnum.UNAVAILABLE]:
    'contract.templates.modal.upload.analysis.reason.UNAVAILABLE',
  [ContractAnalysisReasonEnum.DOCUMENT_TOO_LONG]:
    'contract.templates.modal.upload.analysis.reason.DOCUMENT_TOO_LONG',
  [ContractAnalysisReasonEnum.INVALID_OUTPUT]:
    'contract.templates.modal.upload.analysis.reason.INVALID_OUTPUT',
  [ContractAnalysisReasonEnum.DOCUMENT_EMPTY]:
    'contract.templates.modal.upload.analysis.reason.DOCUMENT_EMPTY',
} as const;

export default function AnalysisStatusBanner({
  status,
  reason,
  variableCount,
}: IAnalysisStatusBannerProps) {
  const { t } = useTranslation();

  if (status === null) return null;

  if (status === ContractAnalysisStatusEnum.AI) {
    return (
      <section
        role="status"
        className="rounded-lg bg-green-50 p-3 text-sm text-green-800 dark:bg-green-900/30 dark:text-green-200"
      >
        {t('contract.templates.modal.upload.analysis.success', {
          count: variableCount,
        })}
      </section>
    );
  }

  return (
    <section
      role="status"
      className="flex flex-col gap-1 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200"
    >
      <strong className="font-semibold">
        {t('contract.templates.modal.upload.analysis.title')}
      </strong>

      {reason && <p>{t(analysisReasonKeys[reason])}</p>}

      {status === ContractAnalysisStatusEnum.FALLBACK ? (
        <p>
          {t('contract.templates.modal.upload.analysis.fallback', {
            count: variableCount,
          })}
        </p>
      ) : (
        <p>{t('contract.templates.modal.upload.analysis.manual')}</p>
      )}
    </section>
  );
}
